<script setup lang="ts">
import { defineProps } from "vue";
import { QiMenChartResponse } from "@/types/calculators";
import { useI18n } from "vue-i18n";
import GradientButton from "@/components/buttons/GradientButton.vue";

import printer from "@/assets/icons/printer.svg";

const props = defineProps<{
  qimenChart: QiMenChartResponse;
  date: string;
  time: string;
  system: string;
  matrixType: string;
}>();

const { t } = useI18n();

const cellsOrder = [4, 9, 2, 3, 5, 7, 8, 1, 6];
const inputParams = [
  {
    label: "calculators.qi-men.labels.date",
    value: props.date,
    isTranslatable: false,
  },
  {
    label: "calculators.qi-men.labels.time",
    value: props.time,
    isTranslatable: false,
  },
  {
    label: "calculators.qi-men.labels.system",
    value: props.system,
    isTranslatable: true,
  },
  {
    label: "calculators.qi-men.labels.matrix-type",
    value: props.matrixType,
    isTranslatable: true,
  },
];
const dayParams = [
  {
    label: "calculators.qi-men.results.date-pillar",
    value: props.qimenChart.pillar,
  },
  {
    label: "calculators.qi-men.results.structure-type",
    value: props.qimenChart.structure_type,
  },
  {
    label: "calculators.qi-men.results.structure-number",
    value: props.qimenChart.structure_num,
  },
];
</script>

<template>
  <div class="flex flex-col md:flex-row md:gap-10 md:mx-auto mb-6">
    <!-- USER INPUT -->
    <div class="grid grid-cols-2 md:flex md:flex-col gap-4 mb-6">
      <div
        v-for="inputParam in inputParams"
        :key="inputParam.label"
        class="bg-white rounded-[20px] px-4 py-2 flex flex-col gap-2 items-start md:w-[200px] md:h-16"
      >
        <span
          class="text-alternative-gray font-semibold text-lg leading-[20px]"
        >
          {{ t(inputParam.label) }}
        </span>
        <span class="text-brown font-semibold text-lg leading-[20px]">
          {{
            inputParam.isTranslatable ? t(inputParam.value) : inputParam.value
          }}
        </span>
      </div>
      <div class="col-span-2 flex max-md:justify-end">
        <GradientButton
          class="w-[125px] h-11 rounded-xl"
          bg-color="bg-brown-gradient"
          hover-bg-color="bg-orange-gradient"
          :text="t('buttons.print')"
        >
          <template #icon>
            <img :src="printer" alt="Print" class="z-10 mr-2" />
          </template>
        </GradientButton>
      </div>
    </div>

    <!-- QI MEN CHART RESULTS -->
    <div
      class="bg-white rounded-[20px] p-6 flex flex-col items-start gap-4 md:max-w-[400px]"
    >
      <p class="font-semibold text-2xl">
        {{ t("calculators.qi-men.results.title") }}
      </p>
      <!-- DAY PARAM -->
      <div class="w-full flex justify-between gap-2">
        <div
          v-for="dayParam in dayParams"
          :key="dayParam.label"
          class="border border-ternary rounded-lg flex flex-col gap-2 items-center p-2 flex-1"
        >
          <span
            class="text-xs md:text-base md:leading-[20px] text-alternative-gray"
          >
            {{ t(dayParam.label) }}
          </span>
          <span
            class="font-semibold text-xl leading-[24px] md:text-2xl text-brown"
          >
            {{ dayParam.value }}
          </span>
        </div>
      </div>
      <p class="font-semibold text-xl leading-[24px]">
        {{ t("calculators.qi-men.results.qi-men-table") }}
      </p>
      <!-- QI MEN TABLE -->
      <div
        class="w-full grid grid-cols-3 overflow-hidden rounded-lg border border-divider"
      >
        <div
          v-for="cellIndex in cellsOrder"
          class="flex flex-wrap justify-center h-full aspect-[100/73] border border-divider"
          :class="cellIndex == 5 ? 'p-4 items-center' : 'pt-[12px] px-[22px]'"
          :key="cellIndex"
        >
          <span
            v-for="(symbol, i) in qimenChart.chart[cellIndex]"
            :key="i"
            class="text-base leading-[20px] md:text-xl md:leading-[24px] text-brown w-1/3 font-normal"
          >
            {{ symbol }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
